import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaCheck, FaWhatsapp, FaCrown, FaStar, FaRocket, FaTrophy } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Plans = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleWhatsApp = (planName) => {
    const message = encodeURIComponent(`¡Hola Deisy! Estoy interesada en el ${planName}. ¿Podrías darme más información?`);
    window.open(`https://wa.me/573192467882?text=${message}`, '_blank');
  };

  const fetchPlans = async () => {
    try {
      // Cache-buster: garantiza que la clienta vea la descripción recién
      // actualizada en el admin sin necesidad de forzar refresh.
      const response = await axios.get(`${API}/public/subscription-plans?_ts=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      const activePlans = response.data?.plans || response.data || [];
      if (activePlans.length > 0) {
        setPlans(activePlans);
      } else {
        // Fallback a la colección legacy
        const legacyRes = await axios.get(`${API}/plans`);
        if (legacyRes.data.length > 0) {
          setPlans(legacyRes.data);
        } else {
          setPlans([
            {
              id: 'plan-basic',
              name: 'Fit Evolution',
              price: 29.99,
              duration: 'mes',
              plan_type: 'basic',
              subtitle: 'Fit Evolution',
              icon: 'rocket',
              features: [
                'Según tus objetivos',
                'Planes personalizados',
                'Soporte por email',
                'Plan nutricional según tus gustos'
              ]
            }
          ]);
        }
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Slug amigable para la URL de checkout. Preserva el `id` interno del plan
  // (que el backend usa para validar permisos) pero muestra un nombre bonito.
  const getFriendlySlug = (plan) => {
    const s = (plan?.slug || '').toLowerCase();
    if (s === 'basic' || plan?.id === 'plan_basic' || plan?.id === 'basic') return 'fit-evolution';
    return s || (plan?.name || plan?.id || '').toLowerCase().replace(/\s+/g, '-');
  };

  const handleSelectPlan = (plan) => {
    navigate(`/checkout/${getFriendlySlug(plan)}`, { state: { plan } });
  };

  const getIcon = (iconName) => {
    switch(iconName) {
      case 'crown': return <FaCrown className="text-xl" />;
      case 'star': return <FaStar className="text-xl" />;
      case 'rocket': return <FaRocket className="text-xl" />;
      case 'trophy': return <FaTrophy className="text-xl" />;
      default: return <FaStar className="text-xl" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div 
          className="w-16 h-16 rounded-full animate-pulse"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)'
        }}
      />
      
      {/* Navbar */}
      <nav className="fixed w-full top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold cursor-pointer" onClick={() => navigate('/')}>
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              DeisyCFit
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white"
            >
              Inicio
            </Button>
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="text-white rounded-full px-6 font-semibold"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              >
                Dashboard
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/login')}
                className="text-white rounded-full px-6 font-semibold"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
              >
                Iniciar Sesión
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 pt-28 pb-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p 
              className="text-xs font-bold uppercase tracking-[0.3em] mb-4"
              style={{ color: 'var(--brand-primary)' }}
            >
              Nuestros planes
            </p>
            <h1 
              className="text-4xl lg:text-6xl font-bold text-white mb-4 uppercase tracking-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Elige Tu{' '}
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
              >
                Plan Ideal
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Selecciona el plan que mejor se ajuste a tus objetivos y comienza tu transformación
            </p>
          </motion.div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, idx) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                className={`relative ${plan.popular ? 'lg:-mt-4 lg:mb-4' : ''}`}
                data-testid={`plan-card-${idx}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span 
                      className="text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider"
                      style={{ 
                        background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
                      }}
                    >
                      Popular
                    </span>
                  </div>
                )}
                
                <div 
                  className="h-full rounded-3xl overflow-hidden transition-all duration-300 flex flex-col hover:border-[var(--brand-primary)]/50"
                  style={{ 
                    background: 'rgba(26, 26, 26, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: plan.popular ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {/* Cover Image */}
                  {(plan.cover_image || plan.background_image) && (
                    <div className="w-full h-44 overflow-hidden">
                      <img 
                        src={plan.cover_image || plan.background_image} 
                        alt={plan.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Header */}
                  <div className="p-6 pb-4">
                    <p 
                      className="text-sm font-medium mb-1"
                      style={{ color: 'var(--brand-primary)' }}
                    >
                      {plan.subtitle || plan.slug || plan.plan_type}
                    </p>
                    <h3 
                      className="text-2xl font-bold text-white"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {plan.name}
                    </h3>
                  </div>
                  
                  {/* Price */}
                  <div className="px-6 pb-4">
                    {/* Precio en COP - Principal */}
                    <div className="flex items-baseline gap-1">
                      <span 
                        className="text-4xl font-bold text-white"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        ${(plan.price_cop || plan.price_monthly_cop || 0).toLocaleString('es-CO')}
                      </span>
                      <span className="text-gray-400 text-sm">COP/{plan.duration || 'mes'}</span>
                    </div>
                    {/* Precio en USD - Referencia */}
                    {(plan.price_monthly || plan.price) > 0 && (
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-xl font-semibold" style={{ color: '#00F0FF' }}>${plan.price_monthly || plan.price}</span>
                        <span className="text-gray-500 text-xs">USD/{plan.duration || 'mes'}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Description - only if different from subtitle */}
                  {plan.description && plan.description !== (plan.subtitle || '') && (
                    <div className="px-6 pb-3">
                      <p className="text-gray-400 text-sm whitespace-pre-line" data-testid={`plan-description-${plan.id || plan.slug || ''}`}>{plan.description}</p>
                    </div>
                  )}
                  
                  {/* Features - ALL shown */}
                  <div className="px-6 pb-4 flex-grow">
                    <ul className="space-y-3">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <FaCheck className="text-[var(--brand-primary)] mt-0.5 flex-shrink-0 text-sm" />
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Action Button - METALIZADO */}
                  <div className="p-6 pt-2">
                    <Button
                      className="w-full rounded-full font-semibold text-white transition-all hover:opacity-90 hover:scale-[1.02] py-5"
                      style={{ 
                        background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
                      }}
                      onClick={() => handleSelectPlan(plan)}
                      data-testid={`select-plan-${idx}`}
                    >
                      Elegir Plan
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-3xl font-bold text-white text-center mb-10 uppercase"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Preguntas{' '}
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              Frecuentes
            </span>
          </h2>
          
          <div className="space-y-4">
            {[
              { q: '¿Cómo funciona el pago?', a: 'Aceptamos Mercado Pago (PSE, tarjetas, Nequi, efectivo) y PayPal. El pago se procesa de forma segura.' },
              { q: '¿Puedo cambiar de plan?', a: 'Sí, puedes cambiar de plan en cualquier momento. Solo contáctame por WhatsApp.' },
              { q: '¿Qué pasa si no me gusta el plan?', a: 'Ofrezco garantía de satisfacción. Si no estás contenta, trabajamos juntas para ajustarlo.' },
              { q: '¿Cuándo empiezo a ver resultados?', a: 'Los primeros cambios se notan entre 2-4 semanas siguiendo el plan correctamente.' }
            ].map((faq, i) => (
              <div 
                key={i} 
                className="rounded-2xl p-6"
                style={{ 
                  background: 'rgba(26, 26, 26, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <h4 className="text-white font-semibold mb-2">{faq.q}</h4>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div 
            className="rounded-3xl p-10"
            style={{ 
              background: 'rgba(26, 26, 26, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <h3 
              className="text-2xl md:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              ¿Tienes dudas?
            </h3>
            <p className="text-gray-400 mb-6">
              Contáctame directamente por WhatsApp y resolveré todas tus preguntas
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* Botón Metalizado Principal */}
              <Button
                className="rounded-full px-8 py-5 font-semibold text-white"
                style={{ 
                  background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
                }}
                onClick={() => handleWhatsApp('planes disponibles')}
              >
                <FaWhatsapp className="mr-2" />
                Contactar por WhatsApp
              </Button>
              {/* Botón Outline Secundario */}
              <Button
                variant="outline"
                className="rounded-full px-8 py-5 font-semibold transition-all"
                style={{ 
                  borderColor: 'var(--brand-primary)',
                  borderWidth: '2px',
                  color: 'var(--brand-primary)',
                  background: 'transparent'
                }}
                onClick={() => navigate('/')}
              >
                Volver al Inicio
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-500 text-sm">© 2025 DeisyCFit. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Plans;
